import {StandardTextFieldProps, TextField} from "@mui/material";
import {Controller, ControllerProps, FieldValues} from "react-hook-form";
import {useIMask} from "react-imask";
import {useEffect, useRef, useState} from "react";

export type ControlledTextFieldProps<TForm extends FieldValues> =
  Required<Pick<ControllerProps<TForm>, "control" | "name">>
  &
  StandardTextFieldProps
  & {
  ControllerProps?: Omit<ControllerProps<TForm>,
    "control" | "name" | "render">;
  mask?: Parameters<typeof useIMask>["0"];
  debounce?: boolean,
  debounceTime?: number,
};

export function ControlledTextField<TForm extends FieldValues = FieldValues>(
  {
    control,
    name,
    mask,
    ControllerProps,
    debounce = false,
    debounceTime = 300,
    ...rest
  }: ControlledTextFieldProps<TForm>
) {
  const [displayedValue, setDisplayedValue] = useState('');
  const displayedValueRef = useRef(displayedValue);
  const timerRef = useRef<NodeJS.Timeout | undefined>(undefined);

  const handleChange = (newValue: string, onChange: (value: string) => void) => {
    setDisplayedValue(newValue);

    if (debounce) {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }

      timerRef.current = setTimeout(() => {
        onChange(newValue);
      }, debounceTime);

    } else {
      onChange(newValue);
    }
  };

  return (
    <Controller
      control={control}
      name={name}
      {...ControllerProps}
      render={({field: {onChange, value}, fieldState: {error}}) => {

        return mask ? (
          <TextFieldWithMask
            mask={mask}
            onChange={(e) => handleChange(e.target.value, onChange)}
            value={displayedValue}
            error={!!error}
            helperText={error && error.message}
            {...rest}
          />
        ) : (
          <TextField
            onChange={(e) => handleChange(e.target.value, onChange)}
            value={displayedValue}
            error={!!error}
            helperText={error && error.message}
            {...rest}
          />
        );
      }}
    />
  );
}

const TextFieldWithMask = (
  props: Omit<StandardTextFieldProps, "onChange"> & {
    onChange: (...event: any[]) => void;
    mask: Parameters<typeof useIMask>["0"];
  }
) => {
  const {mask, onChange, ...textFieldProps} = props;

  const [value, setValue] = useState('');

  const {ref} = useIMask(mask, {
    onAccept: (value, mask) => {
      setValue(value);

      const event = {
        target: { name: props.name, value }
      };

      onChange(event);
    }
  });

  return <TextField {...textFieldProps} value={value} inputRef={ref}/>;
};
